import React, { CSSProperties, ReactNode, useState } from 'react';
import { useSetContentTypeMutation, useSetContentTypeThumbnailMutation } from '../../../generated/graphql';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { FormField, FormFileUpload, FormTextArea } from '../../form';
import styles from './NewContentTypeDialog.module.css';

type NewContentTypeDialogProps = {
    onClose: () => void,
    contentId: string,
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const NewContentTypeDialog = (props: NewContentTypeDialogProps) => {
    const { style, visible, contentId, onClose } = props;
    const [createType, createTypeResult] = useSetContentTypeMutation();
    const [setContentTypeThumbnail, setContentTypeThumbnailResult] = useSetContentTypeThumbnailMutation();
    const [name, setName] = useState('');
    const [settings, setSettings] = useState('{}');
    const [description, setDescription] = useState('');
    const [thumbnail, setThumbnail] = useState<File | null>(null);
    const [createdTypeId, setCreatedTypeId] = useState('');
    const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
    const [validationError, setValidationError] = useState('');

    const handleSubmit = async () => {
        const trimmedName = name.trim();
        const trimmedSettings = settings.trim();

        if (!trimmedName) {
            setValidationError('Type Name is required.');
            return;
        }

        let normalizedSettings = '{}';
        if (trimmedSettings) {
            try {
                normalizedSettings = JSON.stringify(JSON.parse(trimmedSettings));
            } catch {
                setValidationError('Settings must contain valid JSON.');
                return;
            }
        }

        setValidationError('');

        try {
            let typeId = createdTypeId;

            if (!typeId) {
                const result = await createType({
                    variables: {
                        id: contentId,
                        type: {
                            name: trimmedName,
                            description: description.trim(),
                            settings: normalizedSettings,
                        }
                    }
                });

                typeId = result.data?.Content?.setType?.id || '';
                if (!typeId) {
                    setValidationError('The type could not be created. Please try again.');
                    return;
                }

                setCreatedTypeId(typeId);
            }

            if (thumbnail) {
                try {
                    setIsUploadingThumbnail(true);
                    const thumbnailResult = await setContentTypeThumbnail({
                        variables: { contentId, typeId, name: thumbnail.name },
                    });
                    const uploadUrl = thumbnailResult.data?.Content?.setTypeThumbnail;

                    if (!uploadUrl) {
                        throw new Error('No thumbnail upload URL was returned.');
                    }

                    const uploadResponse = await fetch(uploadUrl, {
                        method: 'PUT',
                        headers: { 'x-ms-blob-type': 'BlockBlob' },
                        body: thumbnail,
                    });
                    if (!uploadResponse.ok) {
                        throw new Error(uploadResponse.statusText || `HTTP ${uploadResponse.status}`);
                    }
                } catch (error) {
                    const detail = error instanceof Error ? ` ${error.message}` : '';
                    throw new Error(`The type was created, but its thumbnail could not be uploaded.${detail}`);
                }
            }

            onClose();
        } catch (error) {
            setValidationError(error instanceof Error ? error.message : 'The type could not be created. Please try again.');
        } finally {
            setIsUploadingThumbnail(false);
        }
    };

    return (
        <DialogBase
            title="Add Type"
            type='OkCancel'
            onClose={onClose}
            isLoading={createTypeResult.loading || setContentTypeThumbnailResult.loading || isUploadingThumbnail}
            onSubmit={handleSubmit}
            validationError={validationError}
            visible={visible}
            className={styles.NewContentTypeDialog}
            style={style}
            data-testid="NewContentTypeDialog">
            <FormField label="Type Name" name="name" value={name} onChange={setName} required disabled={!!createdTypeId} />
            <FormTextArea label="Settings (JSON)" name="settings" value={settings} onChange={setSettings} disabled={!!createdTypeId} />
            <FormTextArea label="Description (optional)" name="description" value={description} onChange={setDescription} disabled={!!createdTypeId} />
            <FormFileUpload
                label="Thumbnail (optional)"
                name="thumbnail"
                file={thumbnail}
                onChange={setThumbnail}
                accept="image/*"
            />
        </DialogBase>
    );
};

export default NewContentTypeDialog;
